import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import capitalize from "lodash/capitalize";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

import { useOrderMutation } from "@web-app/api";
import {
  Button,
  DateInput,
  DateTimeTz,
  Field,
  Header,
  Label,
  SelectInput,
  Tooltip,
} from "@web-app/components";
import { Order, TimezoneType } from "@web-app/types";
import http from "@web-app/utils/http";

const expediteFormSchema = z.object({
  order_fulfillment_method: z.enum(["catering", "on_demand"], {
    required_error: "Please select a fulfillment method",
  }),
});

type ExpediteFormData = z.infer<typeof expediteFormSchema>;

type QuoteResponse = {
  delivery_fee: number;
  expires: string;
  tip: number;
};

const preValidation = (orders: Order[]) => {
  const errors: string[] = [];
  const missingFields: Set<string> = new Set();

  for (const order of orders) {
    if (dayjs(order.scheduledStartDateTime).isBefore(dayjs())) {
      errors.push(
        "Delivery schedule start time must be in the future. At least 30 minutes from now is recommended.",
      );
      missingFields.add("scheduledStartDateTime");
    }
  }
  return { errors, missingFields };
};

const getTimezone = (orders: Order[]) => {
  if (orders.length > 0) {
    return orders[0].timezone;
  }
  return "system";
};

export function OrderExpediteDetails({
  entity,
  entityId,
  onClose,
  onCreateDelivery,
  relatedOrders,
}: {
  entity: "order" | "route";
  entityId: string;
  onClose: () => void;
  onCreateDelivery?: (deliveryId: string) => void;
  relatedOrders: Order[];
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isQuotationFetched, setIsQuotationFetched] = useState(false);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [localOrders, setLocalOrders] = useState<Order[]>(relatedOrders);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Store draft values separately - don't update localOrders until save
  const [draftValues, setDraftValues] = useState<
    Record<string, { scheduledStartDateTime?: Date }>
  >(() =>
    relatedOrders.reduce(
      (acc, order) => ({
        ...acc,
        [order.id]: {
          scheduledStartDateTime: order.scheduledStartDateTime,
        },
      }),
      {},
    ),
  );

  const { updateOrder } = useOrderMutation();

  const validationResult = preValidation(localOrders);
  const preValidationErr = validationResult.errors;
  const missingFields = validationResult.missingFields;

  const handleDraftChange = (
    orderId: string,
    field: "scheduledStartDateTime",
    value: Date,
  ) => {
    setHasUnsavedChanges(true);
    setDraftValues((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [field]: value,
      },
    }));
  };

  const { control, handleSubmit } = useForm<ExpediteFormData>({
    defaultValues: {
      order_fulfillment_method: "catering",
    },
    mode: "onChange",
    resolver: zodResolver(expediteFormSchema),
  });

  const timezone = getTimezone(relatedOrders);

  const getExpediteQuotation = async (formData: ExpediteFormData) => {
    setIsLoading(true);
    try {
      const data = await toast.promise(
        http.post(
          `/integrations/expedite/quote?id=${entityId}&entity=${entity}`,
          {
            order_fulfillment_method: formData.order_fulfillment_method,
          },
        ),
        {
          error: (err) => {
            const resp = err.response?.data;
            return (
              resp?.data?.message ||
              resp?.message ||
              "Failed to fetch Expedite quotation"
            );
          },
          loading: "Fetching Expedite quotation...",
          success: "Expedite quotation fetched",
        },
      );

      setIsQuotationFetched(true);
      setQuote(data.data.data);
    } catch {
      // Error is already handled by toast.promise
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveOrderChanges = async () => {
    setIsLoading(true);
    try {
      // First update localOrders with draft values
      const updatedOrders = localOrders.map((order) => {
        const draft = draftValues[order.id];
        return {
          ...order,
          details: {
            ...order.details,
          },
          scheduledStartDateTime:
            draft.scheduledStartDateTime || order.scheduledStartDateTime,
        };
      });

      setLocalOrders(updatedOrders);

      // Then save to backend
      await Promise.all(
        updatedOrders.map((order) =>
          toast.promise(
            updateOrder.mutateAsync({
              id: order.id,
              scheduledStartDateTime: order.scheduledStartDateTime,
            }),
            {
              error: "Failed to update order",
              loading: `Updating order ${order.displayId || order.externalId}...`,
              success: `Order ${order.displayId || order.externalId} updated`,
            },
          ),
        ),
      );
      setHasUnsavedChanges(false);
    } catch {
      // Error already handled by toast.promise
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptQuote = async () => {
    setIsLoading(true);
    await toast.promise(
      http.post(
        `/integrations/expedite/accept?id=${entityId}&entity=${entity}`,
      ),
      {
        error: (err) => {
          onClose();
          const errList = Object.values(err.response?.data?.metadata ?? {}).map(
            (v) => capitalize(v as string),
          );
          const errMsg = `${err.response?.data?.message} ${errList.join("\n")}`;
          return errMsg.trim() || "Failed to create delivery";
        },
        loading: "Sending order to expedite...",
        success: (resp) => {
          onCreateDelivery?.(resp.data.id);
          return "Order sent to expedite";
        },
      },
    );
    setIsLoading(false);
    onClose();
  };

  return (
    <div className="absolute bg-white h-full w-full z-30 flex flex-col">
      {/* Sticky Header */}
      <Header
        title={`Send ${entity === "order" ? "Order" : "Route"} to Expedite`}
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={onClose}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {preValidationErr?.length > 0 && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="h-5 w-5 text-red-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-2">
                    We need a few more details to continue
                  </h3>
                  <ul className="space-y-1.5">
                    {preValidationErr.map((msg, idx) => (
                      <li
                        key={idx}
                        className="text-sm text-red-700 flex items-start gap-2"
                      >
                        <span className="font-medium text-red-600 flex-shrink-0">
                          {idx + 1}.
                        </span>
                        <span>{msg}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Show order detail fields when validation fails */}
            <div className="w-full bg-white rounded-md">
              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700">
                  Please provide the missing information:
                </p>
                {localOrders.map((order, idx) => (
                  <div
                    key={order.id}
                    className="border border-gray-200 rounded-lg p-3 space-y-3"
                  >
                    {localOrders.length > 1 && (
                      <div className="text-xs font-semibold text-gray-600 mb-2">
                        Order {idx + 1}: {order.displayId || order.externalId}
                      </div>
                    )}

                    {missingFields.has("scheduledStartDateTime") && (
                      <Field className="w-full">
                        <Label>Scheduled Start Time</Label>
                        <DateInput
                          value={
                            draftValues[order.id]?.scheduledStartDateTime
                              ? new Date(
                                  draftValues[order.id].scheduledStartDateTime!,
                                )
                              : new Date()
                          }
                          onChange={(date) => {
                            if (date) {
                              handleDraftChange(
                                order.id,
                                "scheduledStartDateTime",
                                date,
                              );
                            }
                          }}
                          minDate={dayjs().add(30, "minutes").toDate()}
                          timezone={order.timezone as TimezoneType}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Must be at least 30 minutes from now
                        </p>
                      </Field>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {isQuotationFetched && preValidationErr?.length === 0 && (
          <div className="w-full bg-white rounded-md space-y-2">
            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">Fee</Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">{`$${quote?.delivery_fee}`}</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pb-2">
              <div className="col-span-1 flex items-center">
                <Label className="text-sm font-medium text-gray-700">Tip</Label>
              </div>
              <div className="col-span-2">
                <div className="text-sm font-medium text-gray-700">{`$${quote?.tip}`}</div>
              </div>
            </div>

            {quote?.expires && (
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-1 flex items-center">
                  <Label className="text-sm font-medium text-gray-700">
                    Expires At
                  </Label>
                </div>
                <div className="col-span-2">
                  <div className="text-sm font-medium text-gray-700">
                    <DateTimeTz
                      start={new Date(quote.expires)}
                      timezone={timezone as TimezoneType}
                      presentation="default"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!isQuotationFetched && preValidationErr?.length === 0 && (
          <div className="w-full bg-white rounded-md">
            <div className="flex flex-col gap-2">
              <Field className="w-full">
                <Label>Order Fulfillment Method</Label>
                <Controller
                  name="order_fulfillment_method"
                  control={control}
                  rules={{
                    required: "Order Fulfillment Method is required",
                  }}
                  render={({ field }) => (
                    <SelectInput
                      options={[
                        { label: "Catering", value: "catering" },
                        {
                          disabled: true,
                          label: "On demand",
                          value: "on_demand",
                        },
                      ]}
                      name="order_fulfillment_method"
                      selected={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                      }}
                      required
                    />
                  )}
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex gap-2">
          {(preValidationErr?.length > 0 || hasUnsavedChanges) &&
            !isQuotationFetched && (
              <Tooltip
                text={
                  hasUnsavedChanges
                    ? "Save your changes before requesting a quote"
                    : "Save order changes to fix validation issues"
                }
              >
                <Button
                  type="button"
                  isLoading={isLoading}
                  onClick={handleSaveOrderChanges}
                >
                  Save Changes
                </Button>
              </Tooltip>
            )}
          {isQuotationFetched && (
            <Tooltip text="Accept quote to send order to Expedite">
              <Button
                type="button"
                isLoading={isLoading}
                onClick={() => handleAcceptQuote()}
              >
                Accept Quote
              </Button>
            </Tooltip>
          )}
          {!isQuotationFetched &&
            preValidationErr?.length === 0 &&
            !hasUnsavedChanges && (
              <Tooltip text="Request quote to see the estimated cost">
                <Button
                  type="button"
                  isLoading={isLoading}
                  onClick={handleSubmit(getExpediteQuotation)}
                >
                  Request Quote
                </Button>
              </Tooltip>
            )}
          <Button type="button" plain onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
